import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { formatSchemaName } from "../../../utils/formatSchemaName";

interface SectionProgressBarProps {
  currentSection: number;
  totalSections: number;
  sectionNames: string[];
  pageConfirmations: { [key: number]: boolean };
  sectionsNeedingAttention: { [key: number]: boolean };
  allowEditing: boolean;
  onSectionClick: (index: number) => void;
  showWarning: (message: string) => void;
}

export const SectionProgressBar: React.FC<SectionProgressBarProps> = ({
  currentSection,
  totalSections,
  sectionNames,
  pageConfirmations,
  sectionsNeedingAttention,
  // allowEditing - kept for API compatibility but not used (navigation is always allowed)
  // showWarning - kept for API compatibility but not used (no restrictions to warn about)
  onSectionClick,
}) => {
  const getStepStatus = (index: number) => {
    if (pageConfirmations[index]) return "completed";
    if (sectionsNeedingAttention[index]) return "attention";
    if (index === currentSection) return "current";
    if (index < currentSection) return "accessible";
    return "locked";
  };

  const canNavigate = (_index: number) => {
    // Allow navigation to any section - user can freely move between sections
    return true;
  };

  const getTooltipText = (index: number, sectionName: string) => {
    const status = getStepStatus(index);
    const formattedName = formatSchemaName(sectionName);
    switch (status) {
      case "completed":
        return `${formattedName} - Completed`;
      case "attention":
        return `${formattedName} - AI Modified, Needs Review`;
      case "current":
        return `${formattedName} - In Progress`;
      default:
        return `${formattedName} - Click to navigate`;
    }
  };

  return (
    <Box sx={{ mb: 4, overflowX: "auto" }}>
      <Box
        sx={{
          position: "relative",
          pt: 0,
          pb: 1,
          width: "100%",
        }}
      >
        {/* Progress track background */}
        {(() => {
          const useDynamicInset = totalSections > 1;
          const inset = useDynamicInset
            ? `calc(100% / (2 * ${totalSections}))`
            : 0;
          return (
            <Box
              sx={{
                position: "absolute",
                bottom: "21px",
                left: inset,
                right: inset,
                height: 6,
                bgcolor: "grey.200",
                borderRadius: 2,
                zIndex: 0,
              }}
            >
              {/* Animated progress fill */}
              <Box
                sx={{
                  height: "100%",
                  bgcolor: "success.main",
                  borderRadius: 2,
                  width:
                    totalSections > 1
                      ? `${(currentSection / (totalSections - 1)) * 100}%`
                      : "100%",
                  transition: "width 0.4s ease-in-out",
                }}
              />
            </Box>
          );
        })()}

        {/* Step indicators */}
        <Box
          sx={{
            position: "relative",
            display: "grid",
            gridTemplateColumns: `repeat(${totalSections}, 1fr)`,
            zIndex: 1,
          }}
        >
          {sectionNames.map((section, index) => {
            const status = getStepStatus(index);
            const isClickable = canNavigate(index);

            return (
              <Tooltip
                key={index}
                title={getTooltipText(index, section)}
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                    opacity: 1,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": isClickable
                      ? {
                          transform: "translateY(-2px)",
                          "& .step-marker": {
                            transform: "scale(1.1)",
                            boxShadow:
                              status === "completed"
                                ? "0 4px 12px rgba(76, 175, 80, 0.4)"
                                : status === "attention"
                                ? "0 4px 12px rgba(255, 152, 0, 0.4)"
                                : "0 4px 12px rgba(25, 118, 210, 0.4)",
                          },
                          "& .step-label": {
                            color:
                              status === "completed"
                                ? "success.main"
                                : status === "attention"
                                ? "warning.dark"
                                : "primary.main",
                          },
                        }
                      : {},
                  }}
                  onClick={() => {
                    onSectionClick(index);
                  }}
                >
                  {/* Section label */}
                  <Typography
                    className="step-label"
                    variant="body2"
                    sx={{
                      fontWeight:
                        status === "current" || status === "attention"
                          ? 600
                          : status === "completed"
                          ? 500
                          : 400,
                      color:
                        status === "current"
                          ? "primary.main"
                          : status === "completed"
                          ? "success.main"
                          : status === "attention"
                          ? "warning.dark"
                          : "text.secondary",
                      fontSize: "0.875rem",
                      mb: 1,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                      px: 0.5,
                      transition: "color 0.2s ease-in-out",
                    }}
                  >
                    {formatSchemaName(section)}
                  </Typography>

                  {/* Step marker */}
                  <Box
                    className="step-marker"
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      bgcolor:
                        status === "completed"
                          ? "success.main"
                          : status === "current"
                          ? "primary.main"
                          : status === "attention"
                          ? "warning.light"
                          : "grey.300",
                      border: "3px solid",
                      borderColor:
                        status === "completed"
                          ? "success.light"
                          : status === "current"
                          ? "primary.light"
                          : status === "attention"
                          ? "warning.main"
                          : "white",
                      boxShadow:
                        status === "current"
                          ? "0 2px 8px rgba(25, 118, 210, 0.3)"
                          : status === "completed"
                          ? "0 2px 8px rgba(76, 175, 80, 0.3)"
                          : status === "attention"
                          ? "0 2px 8px rgba(255, 152, 0, 0.4)"
                          : "0 1px 3px rgba(0,0,0,0.1)",
                      transition: "all 0.3s ease-in-out",
                    }}
                  >
                    {status === "completed" ? (
                      <CheckCircleIcon
                        sx={{
                          fontSize: 20,
                          color: "white",
                        }}
                      />
                    ) : status === "attention" ? (
                      <>
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            color: "warning.dark",
                          }}
                        >
                          {index + 1}
                        </Typography>
                        {/* Attention badge */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: -4,
                            right: -4,
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            bgcolor: "warning.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            color: "white",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                          }}
                        >
                          !
                        </Box>
                      </>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          color:
                            status === "current" ? "white" : "text.secondary",
                        }}
                      >
                        {index + 1}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
